import {
  createPaginatedSupabaseQuery,
  createSearchSupabaseQuery,
  spawnCreateApiFunction,
  spawnDeleteApiFunction,
  spawnGetApiFunction,
  spawnUpdateApiFunction,
  type ApiResourceConfig,
  type SearchOptionFilters,
} from "@/utils/api";
import { type QueryPaginationFilters } from "@/utils/pagination";

const table = "parts";
const queryPaginated = `
  *,
  part_model:part_models(
    *,
    part_manufacturer:part_manufacturers(*),
    part_category:part_categories(*),
    constraints:part_model_constraints(*)
  ),
  part_locations:part_to_part_locations!part_to_part_locations_part_id_fkey(
    quantity,
    part_location:part_locations(
      *,
      part_location_template:part_location_templates(*)
    )
  ),
  search_pm1:part_models(name),
  search_pm2:part_models(description),
  search_pm3:part_models(ref)
`;
const queryPaginatedWithDevices = `
  *,
  device:devices!inner(
    *,
    device_tags(*),
    manufacturer:manufacturers(*),
    model:device_models(*),
    connections_to_devices(*)
  ),
  part_model:part_models(
    *,
    part_manufacturer:part_manufacturers(*),
    part_category:part_categories(*),
    constraints:part_model_constraints(*)
  ),
  part_locations:part_to_part_locations!part_to_part_locations_part_id_fkey(
    quantity,
    part_location:part_locations(
      *,
      part_location_template:part_location_templates(*)
    )
  ),
  search_pm1:part_models(name),
  search_pm2:part_models(description),
  search_pm3:part_models(ref)
`;
const queryCurrent = `
  *,
  deployment:deployments(name),
  device:devices(name),
  part_model:part_models(
    *,
    part_manufacturer:part_manufacturers(*),
    part_category:part_categories(*),
    constraints:part_model_constraints(*)
  ),
  part_inventories(
    *,
    part_locations:part_inventories_to_locations(
      *,
      part_location:part_locations(
        *,
        part_location_template:part_location_templates(*)
      )
    ),
    maintenance_item:maintenance_items(*),
    source_part_history:parts_history!part_inventories_source_part_id_fkey(
      *,
      deployment:deployments(name),
      device:devices(name),
      part_model:part_models(name),
      part:parts(*)
    ),
    target_part_history:parts_history!part_inventories_target_part_id_fkey(
      *,
      deployment:deployments(name),
      device:devices(name),
      part_model:part_models(name),
      part:parts(*)
    )
  ),
  parts_history(
    *,
    device:devices(*),
    maintenance_item:maintenance_items(*)
  ),
  part_locations:part_to_part_locations!part_to_part_locations_part_id_fkey(
    quantity,
    part_location:part_locations(
      *,
      part_location_template:part_location_templates(*)
    )
  )
`;
export const apiConfigParts: ApiResourceConfig<typeof table, typeof queryPaginated> = {
  table,
  query: queryPaginated,
  searchables: ["ref", "description", "search_pm1.name", "search_pm2.description", "search_pm3.ref"],
  sortables: [
    "ref",
    "quantity",
    "average_price",
    "created_at",
    "updated_at",
    "executed_at",
    "archived_at",
    "part_model.name",
  ],
};

export const getPart = spawnGetApiFunction({ table, query: queryCurrent });
export const createPart = spawnCreateApiFunction({ table, query: queryCurrent });
export const updatePart = spawnUpdateApiFunction({ table, query: queryCurrent });
export const deletePart = spawnDeleteApiFunction({ table });

export async function getPaginatedParts({ dataTableOptions, customFilters, customOrFilters }: QueryPaginationFilters) {
  const { data, count, error } = await createPaginatedSupabaseQuery({
    baseOptions: {
      table,
      query: queryPaginated,
      searchables: apiConfigParts.searchables,
      sortables: apiConfigParts.sortables,
      filters: [
        ["archived_at", "is", null],
        ["transient_id", "is", null],
      ],
    },
    dataTableOptions,
    customFilters,
    customOrFilters,
  });
  if (error) throw error;
  return { data, count };
}

export async function getPaginatedPartsWithDevices({
  dataTableOptions,
  customFilters,
  customOrFilters,
}: QueryPaginationFilters) {
  const { data, count, error } = await createPaginatedSupabaseQuery({
    baseOptions: {
      table,
      query: queryPaginatedWithDevices,
      searchables: apiConfigParts.searchables,
      sortables: apiConfigParts.sortables,
      filters: [
        ["archived_at", "is", null],
        ["transient_id", "is", null],
      ],
    },
    dataTableOptions,
    customFilters,
    customOrFilters,
  });
  if (error) throw error;
  return { data, count };
}

export async function getPartsBySearch({ searchOptions, customFilters, customOrFilters, limit }: SearchOptionFilters) {
  const { data, count, error } = await createSearchSupabaseQuery({
    baseOptions: {
      table,
      query: queryPaginated,
      searchables: apiConfigParts.searchables,
      sortables: apiConfigParts.sortables,
    },
    searchOptions,
    customFilters,
    customOrFilters,
    limit,
  });

  if (error) throw error;
  return { data, count };
}
